import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Alert, Badge, Button, Card, Col, Modal, Row, Spinner, Table,
} from 'react-bootstrap'
import { toast } from 'react-toastify'
import api from '../../services/api'

/**
 * Facturación recurrente: configurar una vez y que el sistema prepare las facturas.
 *
 * Tres cosas que esta pantalla tiene que dejar claras, porque son las que confunden:
 *
 * - **`veces` no es lo mismo que «por adelantado»**: la primera es cuántas facturas emitirá en
 *   total, la segunda cuántas están preparadas a la vez.
 * - **un borrador no es un CFDI**: no tiene folio hasta que se timbra.
 * - **un periodo sin fecha válida avisa**, no se mueve solo: si el mes no tiene día 31, el
 *   sistema no decide por el contribuyente.
 */

const COLOR_ESTADO = {
  programada: 'secondary',
  modificada: 'info',
  en_proceso: 'primary',
  timbrada: 'success',
  resultado_incierto: 'warning',
  error: 'danger',
  bloqueada_sin_timbres: 'danger',
  sin_fecha_valida: 'warning',
  omitida: 'dark',
}

const fecha = (iso) => {
  if (!iso) return '—'
  // Partir la cadena en vez de `new Date(iso)`: el constructor lee 'YYYY-MM-DD' como UTC y en
  // México muestra el día anterior. Es el mismo cuidado que en el resto del módulo.
  const [a, m, d] = String(iso).split('-')
  return `${d}/${m}/${a}`
}

const dinero = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`

export default function RecurringInvoices() {
  const qc = useQueryClient()
  const [modal, setModal] = useState(null)
  const [elegida, setElegida] = useState(null)

  const { data: recurrencias, isLoading } = useQuery(
    'recurrencias',
    async () => {
      const r = await api.get('/financial/recurrencias/')
      return r.data.results || r.data || []
    }
  )

  // Los números y el calendario se calculan en el backend: contar estados aquí obligaría a
  // traerse todas las ejecuciones de todas las recurrencias para mostrar seis cifras.
  const { data: panel } = useQuery(
    'recurrencias-panel',
    async () => (await api.get('/financial/recurrencias/panel/')).data
  )
  const { data: atencion } = useQuery(
    'recurrencias-atencion',
    async () => (await api.get('/financial/recurrencias/requieren_atencion/')).data
  )

  const refrescar = () => {
    qc.invalidateQueries('recurrencias')
    qc.invalidateQueries('recurrencia-ejecuciones')
    qc.invalidateQueries('recurrencias-panel')
    qc.invalidateQueries('recurrencias-atencion')
  }

  const accion = useMutation(
    async ({ id, que }) => (await api.post(`/financial/recurrencias/${id}/${que}/`)).data,
    {
      onSuccess: (data, { que }) => {
        // `generar` devuelve avisos que importan --un periodo sin fecha válida, una variable sin
        // resolver--: tragárselos dejaría al usuario creyendo que todo salió bien.
        if (que === 'generar') {
          const creadas = data.creadas?.length || 0
          if (creadas) toast.success(`${creadas} borrador${creadas > 1 ? 'es' : ''} preparado${creadas > 1 ? 's' : ''}`)
          else if (data.sin_cambios) toast.info('No hacía falta preparar ninguno')
          ;(data.avisos || []).forEach((a) => toast.warning(a, { autoClose: 12000 }))
        } else {
          toast.success('Recurrencia actualizada')
        }
        refrescar()
      },
      onError: (e) => toast.error(
        e.response?.data?.estado || e.response?.data?.detail || 'No se pudo completar la acción'),
    }
  )

  if (isLoading) return <Card body><Spinner size="sm" /> Cargando recurrencias…</Card>

  const lista = recurrencias || []

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-1">Facturación recurrente</h5>
          <div className="text-muted small">
            Configura una renta o una iguala una sola vez: el sistema prepara las facturas de
            cada periodo y resuelve el mes en el concepto.
          </div>
        </div>
        {/* No hay botón de «nueva» a propósito: una recurrencia nace de una factura.
            Se crea desde Facturas → Repetir, con todo lo que esa factura ya tiene. */}
        <div className="small text-muted text-end" style={{ maxWidth: '22rem' }}>
          Para crear una, ve a <strong>Facturas</strong>, busca la que quieras repetir y pulsa
          <strong> Repetir</strong>.
        </div>
      </div>

      {/* Lo que requiere intervención va ARRIBA de todo: el principio del requerimiento es
          supervisión por excepción, y un aviso debajo de la lista no se ve. */}
      {(atencion || []).length > 0 && (
        <Alert variant="danger">
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className="bi bi-exclamation-octagon-fill"></i>
            <strong>
              {atencion.length} periodo{atencion.length > 1 ? 's' : ''} necesita
              {atencion.length > 1 ? 'n' : ''} tu atención
            </strong>
          </div>
          <Table size="sm" className="mb-0 bg-white">
            <tbody>
              {atencion.slice(0, 10).map((e) => (
                <tr key={e.id}>
                  <td>{e.plantilla_nombre}</td>
                  <td>{e.periodo}</td>
                  <td>
                    <Badge bg={COLOR_ESTADO[e.estado] || 'secondary'}>{e.estado_etiqueta}</Badge>
                  </td>
                  <td className="small text-muted">{e.motivo}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {atencion.some((e) => e.estado === 'resultado_incierto') && (
            <div className="small mt-2">
              {/* El único estado que puede acabar en dos CFDI por una operación. El contador
                  tiene que saber por qué no se reintentó solo. */}
              <strong>Sin respuesta del PAC:</strong> antes de reintentar hay que verificar si el
              CFDI ya existe. Un segundo intento a ciegas emitiría dos comprobantes por la misma
              operación.
            </div>
          )}
        </Alert>
      )}

      {panel && (
        <Row className="mb-3 text-center">
          <Col>
            <Card body className="py-2">
              <div className="h4 mb-0">{panel.recurrencias.activas}</div>
              <div className="small text-muted">activas</div>
            </Card>
          </Col>
          <Col>
            <Card body className="py-2">
              <div className="h4 mb-0">{panel.recurrencias.automaticas}</div>
              <div className="small text-muted">timbran solas</div>
            </Card>
          </Col>
          <Col>
            <Card body className="py-2">
              <div className="h4 mb-0">{panel.periodos.programados}</div>
              <div className="small text-muted">preparadas</div>
            </Card>
          </Col>
          <Col>
            <Card body className="py-2">
              <div className="h4 mb-0">{panel.periodos.timbrados}</div>
              <div className="small text-muted">timbradas</div>
            </Card>
          </Col>
          <Col>
            <Card body className="py-2">
              <div className={`h4 mb-0 ${panel.periodos.requieren_atencion ? 'text-danger' : ''}`}>
                {panel.periodos.requieren_atencion}
              </div>
              <div className="small text-muted">requieren atención</div>
            </Card>
          </Col>
        </Row>
      )}

      {(panel?.calendario || []).length > 0 && (
        <Card className="mb-3">
          <Card.Header className="py-2">
            <strong className="small">Próximas emisiones</strong>
          </Card.Header>
          <Card.Body className="py-2 d-flex flex-wrap gap-3">
            {panel.calendario.slice(0, 8).map((f) => (
              <div key={f.fecha} className="small">
                <strong>{fecha(f.fecha)}</strong>
                <span className="text-muted"> — {f.cuantas} factura{f.cuantas > 1 ? 's' : ''}</span>
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      {lista.length === 0 && (
        <Alert variant="light" className="border">
          <p className="mb-2">
            Todavía no hay ninguna. Una recurrencia sirve cuando la misma factura se repite cada
            periodo: arrendamiento, iguala, colegiatura, una venta que no cambia.
          </p>
          <p className="mb-0 small text-muted">
            Se crean desde <strong>Facturas</strong>: haz la factura como siempre —con sus
            conceptos, sus impuestos, lo que lleve— y pulsa <strong>Repetir</strong>. Se repetirá
            tal cual.
          </p>
        </Alert>
      )}

      {lista.map((r) => (
        <Card key={r.id} className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <strong>{r.nombre}</strong>
              <Badge bg={r.estado === 'activa' ? 'success' : r.estado === 'pausada' ? 'warning' : 'secondary'}
                     className="ms-2">
                {r.estado}
              </Badge>
              {r.modo_timbrado === 'automatico' && (
                <Badge bg="info" className="ms-1">timbra solo</Badge>
              )}
              <div className="small text-muted mt-1">
                {r.receptor_nombre} · {r.periodicidad} · día {r.dia_programado} · serie {r.serie}
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button size="sm" variant="outline-primary"
                      onClick={() => accion.mutate({ id: r.id, que: 'generar' })}
                      disabled={accion.isLoading || r.estado !== 'activa'}>
                Preparar siguientes
              </Button>
              {r.estado === 'activa' && (
                <Button size="sm" variant="outline-warning"
                        onClick={() => accion.mutate({ id: r.id, que: 'pausar' })}>
                  Pausar
                </Button>
              )}
              {r.estado === 'pausada' && (
                <Button size="sm" variant="outline-success"
                        onClick={() => accion.mutate({ id: r.id, que: 'reanudar' })}>
                  Reanudar
                </Button>
              )}
              <Button size="sm" variant="outline-secondary"
                      onClick={() => { setElegida(r); setModal('periodos') }}>
                Ver periodos
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="py-2">
            <Row className="text-center small">
              <Col>
                <div className="h5 mb-0">{r.emitidas}</div>
                <div className="text-muted">timbradas</div>
              </Col>
              <Col>
                <div className="h5 mb-0">{r.pendientes}</div>
                <div className="text-muted">preparadas</div>
              </Col>
              <Col>
                {/* `veces` y «por adelantado» separados a propósito: confundirlos es preparar
                    facturas que nadie pidió. */}
                <div className="h5 mb-0">{r.veces ?? '∞'}</div>
                <div className="text-muted">contratadas</div>
              </Col>
              <Col>
                <div className="h5 mb-0">{r.borradores_por_adelantado}</div>
                <div className="text-muted">por adelantado</div>
              </Col>
              <Col>
                <div className="h5 mb-0">{fecha(r.proxima_fecha)}</div>
                <div className="text-muted">siguiente</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ))}

      {modal === 'periodos' && elegida && (
        <Periodos recurrencia={elegida}
                  onHide={() => { setModal(null); setElegida(null) }}
                  onCambio={refrescar} />
      )}
    </div>
  )
}

/** Los periodos de una recurrencia: su borrador, su estado y lo que se puede hacer. */
function Periodos({ recurrencia, onHide, onCambio }) {
  const { data: ejecuciones, isLoading, refetch } = useQuery(
    ['recurrencia-ejecuciones', recurrencia.id],
    async () => {
      const r = await api.get(`/financial/recurrencias-ejecuciones/?plantilla=${recurrencia.id}`)
      const filas = r.data.results || r.data || []
      return filas.filter((e) => e.plantilla === recurrencia.id)
    }
  )

  const omitir = useMutation(
    async (id) => (await api.post(`/financial/recurrencias-ejecuciones/${id}/omitir/`)).data,
    {
      onSuccess: () => {
        toast.success('Periodo omitido. Consume su turno del contrato.')
        refetch()
        onCambio()
      },
      onError: (e) => toast.error(e.response?.data?.error || 'No se pudo omitir'),
    }
  )

  return (
    <Modal show onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{recurrencia.nombre} — periodos</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? <><Spinner size="sm" /> Cargando…</> : (
          <Table size="sm" responsive hover>
            <thead>
              <tr>
                <th>#</th><th>Periodo</th><th>Programada</th><th>Folio</th>
                <th className="text-end">Total</th><th>Estado</th><th></th>
              </tr>
            </thead>
            <tbody>
              {(ejecuciones || []).length === 0 && (
                <tr><td colSpan={7} className="text-center text-muted py-3">
                  Ninguno preparado todavía. Usa <em>Preparar siguientes</em>.
                </td></tr>
              )}
              {(ejecuciones || []).map((e) => (
                <tr key={e.id}>
                  <td>{e.numero_repeticion}</td>
                  <td>{e.periodo}</td>
                  <td>{fecha(e.fecha_programada)}</td>
                  <td>
                    {/* Un borrador recurrente no tiene folio real hasta que se timbra: su
                        marcador `REC-…` no consume consecutivo. Mostrarlo como folio sería
                        decirle al contador que ya gastó un número. */}
                    {e.factura_folio?.startsWith('REC-')
                      ? <span className="text-muted">sin folio aún</span>
                      : (e.factura_folio || '—')}
                  </td>
                  <td className="text-end">{e.factura_total ? dinero(e.factura_total) : '—'}</td>
                  <td>
                    <Badge bg={COLOR_ESTADO[e.estado] || 'secondary'}>{e.estado_etiqueta}</Badge>
                    {e.estado === 'sin_fecha_valida' && e.snapshot?.motivo && (
                      <div className="small text-muted mt-1">{e.snapshot.motivo}</div>
                    )}
                  </td>
                  <td className="text-end">
                    {e.estado !== 'timbrada' && e.estado !== 'omitida' && (
                      <Button size="sm" variant="outline-dark"
                              onClick={() => omitir.mutate(e.id)}
                              disabled={omitir.isLoading}>
                        Omitir
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <div className="small text-muted">
          Un periodo omitido <strong>consume su turno</strong>: una recurrencia de 12 veces
          termina a las 12, se usen o se brinquen.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  )
}

